import { memo, useCallback, useMemo } from "react";
import { useQuery } from "@/lib/convex";
import { api } from "@convex/_generated/api";
import { MultiSelectTags } from "../components/MultiSelectTags";
import { SinglePersonPicker } from "../components/SinglePersonPicker";
import {
  LuAlignLeft,
  LuCalendar,
  LuCheck,
  LuChevronDown,
  LuChevronUp,
  LuCircleAlert,
  LuClock,
  LuCopyPlus,
  LuGripVertical,
  LuHash,
  LuLink,
  LuList,
  LuMail,
  LuPlus,
  LuSlidersHorizontal,
  LuToggleLeft,
  LuTrash2,
  LuType,
  LuUpload,
} from "react-icons/lu";
import type { IconType } from "react-icons";
import {
  Box,
  Button,
  Field,
  Flex,
  Heading,
  HStack,
  Icon,
  IconButton,
  Input,
  Menu,
  NativeSelect,
  Portal,
  Select,
  SimpleGrid,
  Stack,
  Switch,
  Tabs,
  Text,
  Textarea,
  Combobox,
  Mark,
  TagsInput,
  createListCollection,
  useCombobox,
  useFilter,
  useTagsInput,
} from "@chakra-ui/react";
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { restrictToParentElement, restrictToVerticalAxis } from "@dnd-kit/modifiers";
import {
  taskDateBasisOptions,
  countReviewers,
  type PersonOption,
  emptyField,
  fieldTypeOptions,
  reviewRoleOptions,
  slugify,
  type ActorKind,
  type TaskDateBasis as TaskDateBasisType,
  type TaskDeadlineChoice,
  type ReviewRole,
  type EditableField,
  type EditableTask,
  type TaskPeople,
  type FieldType,
  type ReviewMode,
} from "./ActivityAdminShared";

// ── Field type presentation ────────────────────────────────────────────────
export const fieldTypeMeta: Record<FieldType, { label: string; icon: IconType; hint: string }> = {
  text: { label: "Short text", icon: LuType, hint: "Single-line text answer" },
  textarea: { label: "Paragraph", icon: LuAlignLeft, hint: "Multi-line text answer" },
  select: { label: "Choice", icon: LuList, hint: "Pick from a list of options" },
  boolean: { label: "Yes / No", icon: LuToggleLeft, hint: "A single yes/no toggle" },
  date: { label: "Date", icon: LuCalendar, hint: "Date picker" },
  time: { label: "Time", icon: LuClock, hint: "Time picker" },
  number: { label: "Number", icon: LuHash, hint: "Numeric answer" },
  range: { label: "Range", icon: LuSlidersHorizontal, hint: "Slider within a range" },
  file: { label: "File upload", icon: LuUpload, hint: "Attach a file" },
  url: { label: "URL", icon: LuLink, hint: "Web link" },
  email: { label: "Email", icon: LuMail, hint: "Email address" },
};

// Display order for the type picker (keeps related types grouped).
const fieldTypeOrder: FieldType[] = fieldTypeOptions.map((o) => o.value);

// ── Immutable array move ─────────────────────────────────────────────────────
export function moveItem<T>(items: T[], from: number, to: number): T[] {
  if (to < 0 || to >= items.length || from === to) return items;
  const next = [...items];
  const [moved] = next.splice(from, 1);
  if (moved === undefined) return items;
  next.splice(to, 0, moved);
  return next;
}

// ── Validation surfaced on the task tabs ─────────────────────────────────────
export function taskIssues(task: EditableTask): string[] {
  const issues: string[] = [];
  if (!task.title.trim()) issues.push("Step needs a title");

  // Both forms, checked the same way. A review form left half-built is as
  // broken as an unfinished task form, and used to pass unnoticed.
  const checkFields = (fields: EditableField[], what: string) => {
    fields.forEach((field, index) => {
      const label = field.label.trim() || `${what} ${index + 1}`;
      if (!field.label.trim()) issues.push(`${what} ${index + 1} needs a label`);
      if (field.type === "select" && field.optionsText.split("\n").filter((o) => o.trim()).length === 0) {
        issues.push(`${label} needs at least one option`);
      }
    });
  };
  checkFields(task.fields, "Field");
  checkFields(task.reviewFields, "Review field");

  // A step completed by a record with nothing ticked is one nothing could
  // ever satisfy. Caught on the tab rather than at save, where it would be
  // a sentence about a task the reader can no longer see.
  if (task.satisfiedByParticipation && task.participationRoles.length === 0) {
    issues.push("Nothing is ticked as counting towards this step");
  }

  // A basis with no number is a half-made choice that saves as no deadline at
  // all -- silently, before this.
  if (task.dueBasis !== "none" && task.dueOffsetDays.trim() === "") {
    issues.push("Deadline needs a number of days");
  }

  return issues;
}

/**
 * One choice for how a step gets done, with the roles spelled out.
 *
 * The roles are items rather than a second select behind "A role": choosing
 * who does something should be one click, and hiding three named roles behind
 * a generic entry made it two and left the panel with a control that appeared
 * only after the first. Grouping is what lets them sit in the same list
 * without reading as five unrelated things.
 *
 * The value encodes the kind and, for a role, which one -- so a selection is
 * always a complete answer rather than half of one.
 */
const completionCollection = createListCollection({
  items: [
    { value: "assignee", label: "The assignee", description: "The person the activity belongs to" },
    { value: "supervisor", label: "Their supervisor", description: "Resolved per person, from the assignee's group" },
    { value: "coSupervisor", label: "Their co-supervisor", description: "Resolved per person, from the recorded co-supervision" },
    ...reviewRoleOptions.map((role) => ({
      value: `role:${role.value}`,
      label: role.label,
      description: "Whoever holds the role at the time",
    })),
    { value: "person", label: "A named person", description: "One specific person, the same for everybody assigned" },
    {
      value: "namedAtAssignment",
      label: "Named when assigned",
      description: "Chosen in the assignment drawer, one person for everybody assigned together",
    },
    {
      // Phrased as an answer to "who", because that is the question, and
      // "a record of taking part" answered a different one. The group label
      // above it says the same thing the other way round.
      value: "participation",
      label: "A record settles it",
      description: "It closes when a matching record of taking part is confirmed",
    },
  ],
});

// The groups answer the heading too -- who does it: a person, whoever holds a
// role, or nobody. That is what lets the last item read as "Nobody: a record
// settles it" without the word Nobody appearing twice.
const completionGroups: Array<{ label: string; values: string[] }> = [
  { label: "A person", values: ["assignee", "supervisor", "coSupervisor", "person", "namedAtAssignment"] },
  { label: "Whoever holds a role", values: reviewRoleOptions.map((role) => `role:${role.value}`) },
  { label: "Nobody", values: ["participation"] },
];

// The same without the catalogue-only answers, for changing one copy.
const COPY_EXCLUDED = new Set(["namedAtAssignment", "participation"]);
const copyCompletionCollection = createListCollection({
  items: completionCollection.items.filter((item) => !COPY_EXCLUDED.has(item.value)),
});
const copyCompletionGroups = completionGroups
  .map((group) => ({ ...group, values: group.values.filter((value) => !COPY_EXCLUDED.has(value)) }))
  .filter((group) => group.values.length > 0);

export function completionValueOf(task: TaskPeople): string {
  if (task.satisfiedByParticipation) {
    return "participation";
  }
  return task.actorKind === "role" ? `role:${task.actorRole}` : task.actorKind;
}

export function applyCompletionValue<T extends TaskPeople>(task: T, value: string): T {
  if (value === "participation") {
    return {
      ...task,
      satisfiedByParticipation: true,
      // The record that satisfies a step is always the subject's own: the
      // pass looks at their participations, and the citing path refuses
      // anybody else's. So there is no actor to choose, and leaving one set
      // would put the step on a supervisor's worklist with nothing on it.
      actorKind: "assignee",
      participationRoles: task.participationRoles.length === 0 ? ["attendee"] : task.participationRoles,
    };
  }
  if (value.startsWith("role:")) {
    return {
      ...task,
      satisfiedByParticipation: false,
      actorKind: "role",
      actorRole: value.slice(5) as ReviewRole,
    };
  }
  return { ...task, satisfiedByParticipation: false, actorKind: value as ActorKind };
}

/**
 * Which end the offset counts from.
 *
 * The two hints were sitting under the control as one line that changed with
 * the selection, which is where a caveat belongs and not where an example
 * does: they are examples of each option, so the reader needs them while
 * choosing rather than after. In the list, marked as examples, they are the
 * fastest way to tell the two apart.
 */
const deadlineBasisCollection = createListCollection({
  items: [
    {
      value: "none",
      label: "No deadline of its own",
      example: "",
    },
    ...taskDateBasisOptions.map((option) => ({
      value: option.value,
      label: option.label,
      example: option.hint,
    })),
  ],
});

const DeadlineBasisSelect = memo(function DeadlineBasisSelect({
  task,
  onChangeTask,
}: {
  task: EditableTask;
  onChangeTask: (updater: (task: EditableTask) => EditableTask) => void;
}) {
  return (
    <Select.Root
      collection={deadlineBasisCollection}
      value={[task.dueBasis]}
      onValueChange={(details) => {
        const next = details.value[0];
        if (next !== undefined) {
          onChangeTask((t) => ({
            ...t,
            dueBasis: next as TaskDeadlineChoice,
            // Choosing "no deadline" clears the number with it, so the two
            // cannot come to disagree about whether there is one.
            dueOffsetDays: next === "none" ? "" : t.dueOffsetDays,
          }));
        }
      }}
      size="sm"
      w="auto"
      minW="60"
      positioning={{ sameWidth: true }}
    >
      <Select.HiddenSelect />
      <Select.Control>
        <Select.Trigger>
          <Select.ValueText />
        </Select.Trigger>
        <Select.IndicatorGroup>
          <Select.Indicator />
        </Select.IndicatorGroup>
      </Select.Control>
      <Portal>
        <Select.Positioner>
          <Select.Content>
            {deadlineBasisCollection.items.map((item) => (
              <Select.Item key={item.value} item={item}>
                <Stack gap={0}>
                  <Select.ItemText>{item.label}</Select.ItemText>
                  {item.example === "" ? (
                    <Text fontSize="xs" color="fg.muted">
                      The activity's due date is the only one
                    </Text>
                  ) : (
                    <Text fontSize="xs" color="fg.muted">
                      <Text as="span" fontStyle="italic">e.g.</Text> {item.example}
                    </Text>
                  )}
                </Stack>
                <Select.ItemIndicator />
              </Select.Item>
            ))}
          </Select.Content>
        </Select.Positioner>
      </Portal>
    </Select.Root>
  );
});

/**
 * Who does a step. `forCopy` is for one assigned copy rather than the
 * catalogue: "named when assigned" has already been answered there, and "a
 * record settles it" is part of what the step is, not of who does it.
 */
export const CompletionSelect = memo(function CompletionSelect({
  value,
  onValueChange,
  forCopy = false,
}: {
  value: string;
  onValueChange: (value: string) => void;
  forCopy?: boolean;
}) {
  const collection = forCopy ? copyCompletionCollection : completionCollection;
  const groups = forCopy ? copyCompletionGroups : completionGroups;

  return (
    <Select.Root
      collection={collection}
      value={[value]}
      onValueChange={(details) => {
        const next = details.value[0];
        if (next !== undefined) {
          onValueChange(next);
        }
      }}
      size="sm"
      maxW="md"
positioning={{ sameWidth: true }}
    >
      <Select.HiddenSelect />
      <Select.Control>
        <Select.Trigger>
          <Select.ValueText placeholder="Choose one" />
        </Select.Trigger>
        <Select.IndicatorGroup>
          <Select.Indicator />
        </Select.IndicatorGroup>
      </Select.Control>
      <Portal>
        <Select.Positioner>
          <Select.Content>
            {groups.map((group) => (
              <Select.ItemGroup key={group.label}>
                {/* Bold and lettered apart from the items: the default
                    weight read as just another row in the list. */}
                <Select.ItemGroupLabel
                  fontSize="xs"
                  fontWeight="bold"
                  textTransform="uppercase"
                  letterSpacing="0.12em"
                  color="fg.muted"
                >
                  {group.label}
                </Select.ItemGroupLabel>
                {group.values.map((groupValue) => {
                  const item = collection.items.find((candidate) => candidate.value === groupValue);
                  if (item === undefined) {
                    return null;
                  }
                  return (
                    <Select.Item key={item.value} item={item}>
                      <Stack gap={0}>
                        <Select.ItemText>{item.label}</Select.ItemText>
                        <Text fontSize="xs" color="fg.muted">{item.description}</Text>
                      </Stack>
                      <Select.ItemIndicator />
                    </Select.Item>
                  );
                })}
              </Select.ItemGroup>
            ))}
          </Select.Content>
        </Select.Positioner>
      </Portal>
    </Select.Root>
  );
});

// Frozen at module scope. `useSensor` keys its descriptor on the options
// OBJECT, so an inline literal makes a new sensor on every render, which makes
// a new sensor array, which re-renders DndContext and with it every sortable
// beneath -- past their memo, because that path is context rather than props.
const POINTER_OPTIONS = { activationConstraint: { distance: 5 } };
const KEYBOARD_OPTIONS = { coordinateGetter: sortableKeyboardCoordinates };
// Same reasoning: a fresh array is a changed prop.

/** File uploads are the actor's business, never the reviewer's. */
const REVIEW_FORM_EXCLUDED_TYPES: FieldType[] = ["file"];
// A default parameter is evaluated on every call, so `= []` would hand the
// memo a new array each render and undo it for the task's own form -- the
// one case that passes nothing.
const NO_EXCLUDED_TYPES: FieldType[] = [];

export const VERTICAL_LIST_MODIFIERS = [restrictToVerticalAxis, restrictToParentElement];

function useDragSensors() {
  return useSensors(
    useSensor(PointerSensor, POINTER_OPTIONS),
    useSensor(KeyboardSensor, KEYBOARD_OPTIONS),
  );
}

// ── Type picker menu, used both to add and (elsewhere) surface types ─────────
export function AddFieldMenu({
  onAdd,
  size = "sm",
  exclude = [],
}: {
  onAdd: (type: FieldType) => void;
  size?: "xs" | "sm";
  /** Types this form cannot hold -- a review form has no file uploads. */
  exclude?: FieldType[];
}) {
  return (
    <Menu.Root positioning={{ placement: "bottom-start" }}>
      <Menu.Trigger asChild>
        <Button size={size} variant="outline" colorPalette="blue">
          <LuPlus />
          Add field
          <LuChevronDown />
        </Button>
      </Menu.Trigger>
      <Portal>
        <Menu.Positioner>
          <Menu.Content minW="60">
            {fieldTypeOrder.filter((type) => !exclude.includes(type)).map((type) => {
              const meta = fieldTypeMeta[type];
              return (
                <Menu.Item key={type} value={type} onClick={() => onAdd(type)}>
                  <Icon as={meta.icon} boxSize={4} color="blue.500" />
                  <Stack gap={0} flex="1">
                    <Text>{meta.label}</Text>
                    <Text fontSize="xs" color="fg.muted">{meta.hint}</Text>
                  </Stack>
                </Menu.Item>
              );
            })}
          </Menu.Content>
        </Menu.Positioner>
      </Portal>
    </Menu.Root>
  );
}

type FieldUpdater = (updater: (field: EditableField) => EditableField) => void;

type FieldCardProps = {
  field: EditableField;
  index: number;
  count: number;
  // Keyed by index rather than closed over it, so one set of callbacks
  // serves every card. A per-card arrow is a new prop on every render,
  // which is what kept every field re-rendering when one label changed.
  onChangeAt: (index: number, updater: (field: EditableField) => EditableField) => void;
  onRemoveAt: (index: number) => void;
  onDuplicateAt: (index: number) => void;
  onMoveAt: (index: number, direction: -1 | 1) => void;
  dragHandleProps?: Record<string, unknown>;
};

// ── A single form-field card (MS-Forms inspired) ─────────────────────────────
const FieldCard = memo(function FieldCard({
  field,
  index,
  count,
  onChangeAt,
  onRemoveAt,
  onDuplicateAt,
  onMoveAt,
  dragHandleProps,
}: FieldCardProps) {
  // Bound to this card's index once, so the body below reads exactly as it
  // did when each card was handed its own closures.
  const onChange: FieldUpdater = (updater) => onChangeAt(index, updater);
  const onRemove = () => onRemoveAt(index);
  const onDuplicate = () => onDuplicateAt(index);
  const onMove = (direction: -1 | 1) => onMoveAt(index, direction);
  const meta = fieldTypeMeta[field.type];
  const options = field.optionsText.length ? field.optionsText.split("\n") : [];
  const setOptions = (next: string[]) => onChange((f) => ({ ...f, optionsText: next.join("\n") }));
  const isNumeric = field.type === "number" || field.type === "range";

  return (
    <Box
      borderWidth="1px"
      borderColor="border"
      borderLeftWidth="3px"
      borderLeftColor={field.required ? "blue.500" : "border"}
      rounded="md"
      bg="bg"
      p={3}
      transition="border-color 0.15s"
    >
      {/* Header: drag handle + read-only type + required + toolbar */}
      <Flex mb={3} align="center" justify="space-between" gap={2}>
        <HStack gap={2} minW={0}>
          <Box
            {...dragHandleProps}
            title="Drag to reorder"
            cursor="grab"
            color="fg.subtle"
            _hover={{ color: "fg.muted" }}
            display="flex"
            alignItems="center"
          >
            <LuGripVertical />
          </Box>
          <HStack gap={1.5} px={2} py={1} borderWidth="1px" borderColor="border" rounded="md" bg="bg.muted">
            <Icon as={meta.icon} boxSize={3.5} color="blue.500" />
            <Text fontSize="xs" fontWeight="medium">{meta.label}</Text>
          </HStack>
        </HStack>
        <HStack gap={2}>
          <Switch.Root
            colorPalette="blue"
            checked={field.required}
            onCheckedChange={(e) => onChange((f) => ({ ...f, required: e.checked }))}
          >
            <Switch.HiddenInput />
            <Switch.Control />
            <Switch.Label fontSize="sm">Required</Switch.Label>
          </Switch.Root>
          <HStack gap={0.5} borderLeftWidth="1px" borderColor="border" pl={2}>
            <IconButton
              aria-label="Move field up"
              title="Move up"
              size="xs"
              variant="ghost"
              color="fg.muted"
              disabled={index === 0}
              onClick={() => onMove(-1)}
            >
              <LuChevronUp />
            </IconButton>
            <IconButton
              aria-label="Move field down"
              title="Move down"
              size="xs"
              variant="ghost"
              color="fg.muted"
              disabled={index === count - 1}
              onClick={() => onMove(1)}
            >
              <LuChevronDown />
            </IconButton>
            <IconButton aria-label="Duplicate field" title="Duplicate field" size="xs" variant="ghost" color="fg.muted" onClick={onDuplicate}>
              <LuCopyPlus />
            </IconButton>
            <IconButton aria-label="Remove field" title="Remove field" size="xs" variant="ghost" colorPalette="red" onClick={onRemove}>
              <LuTrash2 />
            </IconButton>
          </HStack>
        </HStack>
      </Flex>

      {/* Label */}
      <Input
        placeholder="Question / field label"
        fontWeight="medium"
        value={field.label}
        onChange={(e) => {
          const label = e.target.value;
          // Keep persisted field keys stable so stored responses keep resolving.
          onChange((f) => ({ ...f, label, key: f.persisted ? f.key : slugify(label) }));
        }}
      />

      {/* Help text */}
      <Box mt={3}>
        <Field.Root>
          <Field.Label fontSize="xs" color="fg.muted">Help text (optional)</Field.Label>
          <Input
            size="sm"
            placeholder="Shown below the field to guide the answer"
            value={field.description}
            onChange={(e) => onChange((f) => ({ ...f, description: e.target.value }))}
          />
        </Field.Root>
      </Box>

      {/* Choice options — inline rows */}
      {field.type === "select" ? (
        <Stack mt={3} gap={2}>
          <Text fontSize="xs" fontWeight="medium" color="fg.muted">Options</Text>
          {options.map((option, optionIndex) => (
            <HStack key={optionIndex} gap={2}>
              <Icon as={LuList} boxSize={4} color="fg.muted" flexShrink={0} />
              <Input
                size="sm"
                placeholder={`Option ${optionIndex + 1}`}
                value={option}
                onChange={(e) =>
                  setOptions(options.map((o, i) => (i === optionIndex ? e.target.value : o)))
                }
              />
              <IconButton
                aria-label="Remove option"
                size="xs"
                variant="ghost"
                color="fg.muted"
                onClick={() => setOptions(options.filter((_, i) => i !== optionIndex))}
              >
                <LuTrash2 />
              </IconButton>
            </HStack>
          ))}
          <Box>
            <Button size="xs" variant="ghost" colorPalette="blue" onClick={() => setOptions([...options, ""])}>
              <LuPlus />
              Add option
            </Button>
          </Box>
        </Stack>
      ) : null}

      {/* File upload: single vs multiple */}
      {field.type === "file" ? (
        <Box mt={3}>
          <Switch.Root
            size="sm"
            checked={field.multiple}
            onCheckedChange={(e) => onChange((f) => ({ ...f, multiple: e.checked }))}
          >
            <Switch.HiddenInput />
            <Switch.Control />
            <Switch.Label fontSize="sm">Allow multiple files</Switch.Label>
          </Switch.Root>
        </Box>
      ) : null}

      {/* Numeric constraints */}
      {isNumeric ? (
        <SimpleGrid mt={3} gap={3} columns={{ base: 1, sm: 3 }}>
          <Field.Root>
            <Field.Label fontSize="xs" color="fg.muted">Min</Field.Label>
            <Input size="sm" type="number" value={field.min} onChange={(e) => onChange((f) => ({ ...f, min: e.target.value }))} />
          </Field.Root>
          <Field.Root>
            <Field.Label fontSize="xs" color="fg.muted">Max</Field.Label>
            <Input size="sm" type="number" value={field.max} onChange={(e) => onChange((f) => ({ ...f, max: e.target.value }))} />
          </Field.Root>
          <Field.Root>
            <Field.Label fontSize="xs" color="fg.muted">Step</Field.Label>
            <Input size="sm" type="number" value={field.step} onChange={(e) => onChange((f) => ({ ...f, step: e.target.value }))} />
          </Field.Root>
        </SimpleGrid>
      ) : null}
    </Box>
  );
});

// Sortable wrapper around a field card. Memoised with the card: the drag
// context re-renders on every pointer move, and a card that cannot skip
// re-renders with it.
const SortableFieldCard = memo(function SortableFieldCard({ id, ...cardProps }: { id: string } & Omit<FieldCardProps, "dragHandleProps">) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
    zIndex: isDragging ? 1 : undefined,
    position: "relative",
  };
  return (
    <Box ref={setNodeRef} style={style}>
      <FieldCard {...cardProps} dragHandleProps={{ ...attributes, ...listeners }} />
    </Box>
  );
});

type TaskUpdater = (updater: (task: EditableTask) => EditableTask) => void;

// ── The editor panel for the currently-selected task ─────────────────────────
/**
 * Pick named reviewers from the directory.
 *
/**
 * The list of questions on one form.
 *
 * Rendered twice per task -- once for what the actor answers, once for what
 * the reviewer answers when signing -- so it takes its list and its setter
 * rather than reaching into the task. `idPrefix` keeps the two drag contexts
 * from sharing sortable ids, which would let a drag in one reorder the other.
 */
const FormFieldsEditor = memo(function FormFieldsEditor({
  heading,
  description,
  fields,
  onChangeFields,
  idPrefix,
  emptyHint = "No fields yet. Pick a type to add one.",
  excludeTypes = NO_EXCLUDED_TYPES,
}: {
  heading: string;
  description: string;
  fields: EditableField[];
  onChangeFields: (updater: (fields: EditableField[]) => EditableField[]) => void;
  idPrefix: string;
  emptyHint?: string;
  excludeTypes?: FieldType[];
}) {
  const sensors = useDragSensors();

  // Stable, so a card that did not change can skip. `map` keeps the identity
  // of every field but the edited one, which is what makes the skip possible
  // at all -- the callbacks only have to stop undoing it.
  const updateField = useCallback(
    (fieldIndex: number, updater: (field: EditableField) => EditableField) =>
      onChangeFields((current) => current.map((f, i) => (i === fieldIndex ? updater(f) : f))),
    [onChangeFields],
  );
  const addField = useCallback(
    (type: FieldType) => onChangeFields((current) => [...current, emptyField(type)]),
    [onChangeFields],
  );
  const removeField = useCallback(
    (fieldIndex: number) => onChangeFields((current) => current.filter((_, i) => i !== fieldIndex)),
    [onChangeFields],
  );
  const duplicateField = useCallback(
    (fieldIndex: number) =>
      onChangeFields((current) => {
      const source = current[fieldIndex];
      if (!source) return current;
      const next = [...current];
      // A duplicate is a brand-new field: it must not inherit the persisted key.
      next.splice(fieldIndex + 1, 0, {
        ...source,
        persisted: false,
        key: source.key ? `${source.key}-copy` : "",
      });
        return next;
      }),
    [onChangeFields],
  );
  const moveField = useCallback(
    (fieldIndex: number, direction: -1 | 1) =>
      onChangeFields((current) => moveItem(current, fieldIndex, fieldIndex + direction)),
    [onChangeFields],
  );

  const handleFieldDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const from = Number(String(active.id).replace(`${idPrefix}-`, ""));
    const to = Number(String(over.id).replace(`${idPrefix}-`, ""));
    onChangeFields((current) => moveItem(current, from, to));
  }, [idPrefix, onChangeFields]);

  // Built once. dnd-kit re-renders every `useSortable` consumer when the
  // items it is given change identity, so a fresh array here re-rendered every
  // field card on every keystroke -- through their memo, which compares props
  // and never sees this. The list only changes when a field is added, removed
  // or moved.
  // Keyed on the LENGTH, not the array. These ids are positional, so
  // editing a field's label cannot change them -- but recomputing on the
  // array made a new list every keystroke, and a new list re-renders every
  // sortable through the context. That is why one label redrew them all.
  const fieldCount = fields.length;
  const fieldIds = useMemo(
    () => Array.from({ length: fieldCount }, (_, i) => `${idPrefix}-${i}`),
    [fieldCount, idPrefix],
  );

  return (
    <Box borderWidth="1px" borderColor="border" rounded="md" bg="bg.muted" p={3}>
      <Box mb={3}>
        <Heading size="sm">{heading}</Heading>
        <Text fontSize="xs" color="fg.muted">{description}</Text>
      </Box>

      {fields.length === 0 ? (
        <Box borderWidth="1px" borderStyle="dashed" borderColor="border" rounded="md" px={4} py={6} textAlign="center">
          <Text mb={3} fontSize="sm" color="fg.muted">{emptyHint}</Text>
          <AddFieldMenu onAdd={addField} exclude={excludeTypes} />
        </Box>
      ) : (
        <Stack gap={3}>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleFieldDragEnd}
            modifiers={VERTICAL_LIST_MODIFIERS}
          >
            <SortableContext items={fieldIds} strategy={verticalListSortingStrategy}>
              <Stack gap={3}>
                {fields.map((field, fieldIndex) => (
                  <SortableFieldCard
                    key={fieldIndex}
                    id={`${idPrefix}-${fieldIndex}`}
                    field={field}
                    index={fieldIndex}
                    count={fields.length}
                    onChangeAt={updateField}
                    onRemoveAt={removeField}
                    onDuplicateAt={duplicateField}
                    onMoveAt={moveField}
                  />
                ))}
              </Stack>
            </SortableContext>
          </DndContext>
          <Box>
            <AddFieldMenu onAdd={addField} exclude={excludeTypes} />
          </Box>
        </Stack>
      )}
    </Box>
  );
});

/**
 * One task's editor.
 *
 * Memoised, and the call site takes care to pass it props that hold their
 * identity: the draft's `tasks` array survives a change to the activity's
 * title, so with stable callbacks this panel -- two drag contexts, a field
 * list and two comboboxes -- does not re-render when somebody types in a box
 * that has nothing to do with it.
 */
export const TaskPanel = memo(function TaskPanel({
  task,
  taskIndex,
  taskCount,
  people,
  otherTasks,
  onChangeTask,
  onRemoveTask,
  onDuplicateTask,
  onMoveTask,
}: {
  people: PersonOption[];
  otherTasks: EditableTask[];
  task: EditableTask;
  taskIndex: number;
  taskCount: number;
  onChangeTask: TaskUpdater;
  onRemoveTask: () => void;
  onDuplicateTask: () => void;
  onMoveTask: (direction: -1 | 1) => void;
}) {
  // One identity each, so the two field editors below can skip. They are the
  // heaviest part of this panel and neither changes when the other does.
  const onChangeFields = useCallback(
    (updater: (fields: EditableField[]) => EditableField[]) =>
      onChangeTask((t) => ({ ...t, fields: updater(t.fields) })),
    [onChangeTask],
  );
  const onChangeReviewFields = useCallback(
    (updater: (fields: EditableField[]) => EditableField[]) =>
      onChangeTask((t) => ({ ...t, reviewFields: updater(t.reviewFields) })),
    [onChangeTask],
  );
  const onChangeCompletion = useCallback(
    (value: string) => onChangeTask((t) => applyCompletionValue(t, value)),
    [onChangeTask],
  );
  const onChangeActorPerson = useCallback(
    (actorPersonId: string | null) => onChangeTask((t) => ({ ...t, actorPersonId })),
    [onChangeTask],
  );
  // All three reviewer kinds arrive together now, from one picker. Setting
  // them also settles the review mode: with nobody to sign there is no mode,
  // and with somebody there has to be one.
  const onChangeReviewers = useCallback(
    (picked: { supervisor: boolean; coSupervisor: boolean; namedAtAssignment: boolean; roles: ReviewRole[]; personIds: string[] }) =>
      onChangeTask((t) => {
        const next = {
          ...t,
          reviewerSupervisor: picked.supervisor,
          reviewerCoSupervisor: picked.coSupervisor,
          reviewerNamedAtAssignment: picked.namedAtAssignment,
          reviewerRoles: picked.roles,
          reviewerPersonIds: picked.personIds,
        };
        return { ...next, reviewMode: countReviewers(next) === 0 ? null : (t.reviewMode ?? "any") };
      }),
    [onChangeTask],
  );

  return (
    <Stack gap={4}>
      {/* Panel header: editable title + required + toolbar */}
      <Flex align="flex-start" justify="space-between" gap={3} flexWrap="wrap">
        <Stack gap={1} flex="1" minW={0}>
          <Text fontSize="xs" fontWeight="semibold" textTransform="uppercase" letterSpacing="0.16em" color="fg.muted">
            Task {taskIndex + 1}
          </Text>
          <Input
            variant="flushed"
            fontSize="lg"
            fontWeight="semibold"
            placeholder="Step title"
            value={task.title}
            onChange={(e) => onChangeTask((t) => ({ ...t, title: e.target.value }))}
          />
        </Stack>
        <HStack gap={2}>
          <Switch.Root
            colorPalette="blue"
            checked={task.required}
            onCheckedChange={(e) => onChangeTask((t) => ({ ...t, required: e.checked }))}
          >
            <Switch.HiddenInput />
            <Switch.Control />
            <Switch.Label fontSize="sm">Required</Switch.Label>
          </Switch.Root>
          <HStack gap={0.5} borderLeftWidth="1px" borderColor="border" pl={2}>
            <IconButton
              aria-label="Move step up"
              title="Move up"
              size="sm"
              variant="ghost"
              color="fg.muted"
              disabled={taskIndex === 0}
              onClick={() => onMoveTask(-1)}
            >
              <LuChevronUp />
            </IconButton>
            <IconButton
              aria-label="Move step down"
              title="Move down"
              size="sm"
              variant="ghost"
              color="fg.muted"
              disabled={taskIndex === taskCount - 1}
              onClick={() => onMoveTask(1)}
            >
              <LuChevronDown />
            </IconButton>
            <IconButton aria-label="Duplicate step" title="Duplicate step" size="sm" variant="ghost" color="fg.muted" onClick={onDuplicateTask}>
              <LuCopyPlus />
            </IconButton>
            <IconButton aria-label="Remove step" title="Remove step" size="sm" variant="ghost" colorPalette="red" onClick={onRemoveTask}>
              <LuTrash2 />
            </IconButton>
          </HStack>
        </HStack>
      </Flex>

      {/* Description */}
      <Field.Root>
        <Field.Label>Description</Field.Label>
        <Textarea
          minH={14}
          value={task.description}
          placeholder="Explain what needs to happen in this step…"
          onChange={(e) => onChangeTask((t) => ({ ...t, description: e.target.value }))}
        />
      </Field.Root>
      {/* A · Doing it. The form lives here, beside the question of who fills
          it in, rather than three blocks further down.

          The band heading IS the label for the control directly beneath it:
          repeating the question as a sub-label said the same thing twice, and
          band B had no such line at all, which is the inconsistency the reader
          spotted. A sub-label now appears only on the SECOND and later
          controls of a band. */}
      <TaskSection letter="A" title="Who does it">
        <CompletionSelect value={completionValueOf(task)} onValueChange={onChangeCompletion} />

        {!task.satisfiedByParticipation && task.actorKind === "person" ? (
          <SinglePersonPicker
            label="Which person"
            people={people}
            selectedPersonId={task.actorPersonId}
            onChange={onChangeActorPerson}
          />
        ) : null}

        {task.satisfiedByParticipation ? (
          <ParticipationCriteriaEditor task={task} onChangeTask={onChangeTask} />
        ) : null}

        {/* A step completed by a record has nothing for anybody to fill in. */}
        {task.satisfiedByParticipation ? null : (
          <FormFieldsEditor
            heading="What they fill in"
            description="The questions whoever does this step has to answer."
            fields={task.fields}
            onChangeFields={onChangeFields}
            idPrefix="field"
          />
        )}
      </TaskSection>

      {/* B · Signing it off. One control for all three reviewer kinds, and the
          review form immediately beneath the people who fill it in. */}
      <TaskSection letter="B" title="Who signs it off" hint="Optional">
        <ReviewerPicker people={people} task={task} onChange={onChangeReviewers} />

        {/* Only meaningful with more than one signature to wait for. */}
        {countReviewers(task) > 1 ? (
          <Stack gap={2}>
            <SubLabel>How many have to approve</SubLabel>
            <NativeSelect.Root size="sm" maxW="sm">
              <NativeSelect.Field
                value={task.reviewMode ?? "any"}
                onChange={(event) =>
                  onChangeTask((t) => ({ ...t, reviewMode: event.target.value as ReviewMode }))}
              >
                <option value="any">Any one of them</option>
                <option value="all">Every one of them</option>
              </NativeSelect.Field>
              <NativeSelect.Indicator />
            </NativeSelect.Root>
          </Stack>
        ) : null}

        {countReviewers(task) > 0 ? (
          <FormFieldsEditor
            heading="What the reviewer records"
            description="Leave empty for a comment alone."
            fields={task.reviewFields}
            onChangeFields={onChangeReviewFields}
            idPrefix="reviewfield"
            emptyHint="No review form. Approving will ask for a comment only."
            // A reviewer's assessment is an answer, not an upload; the renderer
            // has no file control either, so offering the type would produce a
            // field nobody could fill in.
            excludeTypes={REVIEW_FORM_EXCLUDED_TYPES}
          />
        ) : null}
      </TaskSection>

      {/* C · Timing, last. It sat in the middle, between the two halves of the
          work above, which is what forced them apart. */}
      <TaskSection letter="C" title="When it is due" hint="Optional">
        <Stack gap={2}>
          <Flex gap={2} align="center" wrap="wrap">
            {/* The number only exists once there is a deadline to measure. */}
            {task.dueBasis === "none" ? null : (
              <>
                <Input
                  type="number"
                  min={0}
                  w="24"
                  size="sm"
                  placeholder="days"
                  value={task.dueOffsetDays}
                  onChange={(event) => onChangeTask((t) => ({ ...t, dueOffsetDays: event.target.value }))}
                />
                <Text fontSize="sm" color="fg.muted">days</Text>
              </>
            )}
            <DeadlineBasisSelect task={task} onChangeTask={onChangeTask} />
          </Flex>
          {task.dueBasis !== "none" && task.dueOffsetDays.trim() === "" ? (
            <Text fontSize="xs" color="fg.error">
              Say how many days, or choose no deadline.
            </Text>
          ) : null}
        </Stack>

        {otherTasks.length > 0 ? (
          <Stack gap={2}>
            <SubLabel hint="Until these are approved it stays visible but locked">Opens after</SubLabel>
            <SimpleGrid gap={2} columns={{ base: 1, xl: 2 }}>
              {otherTasks.map((other) => (
                <Switch.Root
                  key={other.localKey}
                  size="sm"
                  colorPalette="blue"
                  checked={task.prerequisiteKeys.includes(other.localKey)}
                  onCheckedChange={(e) =>
                    onChangeTask((t) => ({
                      ...t,
                      prerequisiteKeys: e.checked === true
                        ? [...t.prerequisiteKeys, other.localKey]
                        : t.prerequisiteKeys.filter((key) => key !== other.localKey),
                    }))
                  }
                >
                  <Switch.HiddenInput />
                  <Switch.Control />
                  <Switch.Label fontSize="sm">{other.title || "Untitled step"}</Switch.Label>
                </Switch.Root>
              ))}
            </SimpleGrid>
          </Stack>
        ) : null}
      </TaskSection>
    </Stack>
  );
});

// ── Sortable task tab (used in the vertical task list) ───────────────────────
export const SortableTaskTab = memo(function SortableTaskTab({
  id,
  index,
  task,
  issues,
}: {
  id: string;
  index: number;
  task: EditableTask;
  issues: number;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
    zIndex: isDragging ? 1 : undefined,
    position: "relative",
  };

  return (
    <HStack ref={setNodeRef} style={style} gap={1} align="stretch">
      <Box
        {...attributes}
        {...listeners}
        title="Drag to reorder"
        cursor="grab"
        color="fg.subtle"
        _hover={{ color: "fg.muted" }}
        display="flex"
        alignItems="center"
        px={0.5}
      >
        <LuGripVertical />
      </Box>
      <Tabs.Trigger
        value={String(index)}
        colorPalette="blue"
        flex="1"
        minW={0}
        h="auto"
        justifyContent="flex-start"
        textAlign="left"
        gap={2}
        px={3}
        py={2.5}
        borderWidth="1px"
        borderColor="border"
        rounded="md"
        bg="bg"
        _selected={{ borderColor: "colorPalette.solid", bg: "colorPalette.subtle" }}
      >
        <Stack gap={1} flex="1" minW={0}>
          <HStack gap={2}>
            <Text fontSize="xs" fontWeight="semibold" textTransform="uppercase" letterSpacing="0.14em" color="fg.muted">
              Step {index + 1}
            </Text>
            {task.required ? <Box boxSize={1.5} rounded="full" bg="blue.500" title="Required" /> : null}
          </HStack>
          <Text truncate fontWeight="medium">{task.title || "Untitled step"}</Text>
          <Text fontSize="xs" color="fg.muted">
            {task.fields.length} field{task.fields.length === 1 ? "" : "s"}
          </Text>
        </Stack>
        {issues > 0 ? (
          <Box title={`${issues} issue${issues === 1 ? "" : "s"}`} display="flex" alignItems="center">
            <Icon as={LuCircleAlert} boxSize={4} color="orange.500" />
          </Box>
        ) : null}
      </Tabs.Trigger>
    </HStack>
  );
});

const PARTICIPATION_ROLE_CHOICES = [
  { value: "attendee", label: "Attended" },
  { value: "posterPresenter", label: "Presented a poster" },
  { value: "speaker", label: "Gave a talk" },
  { value: "invitedLecturer", label: "Invited lecture" },
  { value: "host", label: "Hosted" },
  { value: "organiser", label: "Organised it" },
];

/**
 * "This step is completed by a record."
 *
 * The roles are a SET, and the control is a set of switches for that reason:
 * "attend a conference" is met by somebody who went and equally by somebody
 * who went and gave a talk. Choosing one role would refuse the honest record
 * of having done more than was asked.
 *
 * The narrowing choices below are alternatives and not a form to fill in --
 * one occasion, any occurrence of a series, or a kind of self-reported
 * occasion -- so leaving them alone means "any occasion in those roles",
 * which is a legitimate requirement rather than an unfinished one.
 */
const ParticipationCriteriaEditor = memo(function ParticipationCriteriaEditor({
  task,
  onChangeTask,
}: {
  task: EditableTask;
  onChangeTask: (updater: (task: EditableTask) => EditableTask) => void;
}) {
  const series = useQuery(api.events.listSeries, {});

  return (
    <Stack gap={3} borderWidth="1px" borderColor="border" p={4} ml={1}>
      <Text fontSize="sm" color="fg.muted">
        The step closes on its own when a matching record is confirmed — a roster the event
        organiser ticks off, or a conference the person records themselves.
      </Text>

      {(
        <Stack gap={4} pt={2}>
          <Box>
            <SubLabel hint="Somebody who did more than was asked should still have it count">
              Which of these count
            </SubLabel>
            <Stack gap={1} mt={2}>
              {PARTICIPATION_ROLE_CHOICES.map((choice) => (
                <Switch.Root
                  key={choice.value}
                  size="sm"
                  colorPalette="blue"
                  checked={task.participationRoles.includes(choice.value)}
                  onCheckedChange={(e) =>
                    onChangeTask((t) => ({
                      ...t,
                      participationRoles: e.checked === true
                        ? [...t.participationRoles, choice.value]
                        : t.participationRoles.filter((role) => role !== choice.value),
                    }))}
                >
                  <Switch.HiddenInput />
                  <Switch.Control />
                  <Switch.Label>{choice.label}</Switch.Label>
                </Switch.Root>
              ))}
            </Stack>
            {task.participationRoles.length === 0 ? (
              <Text mt={2} fontSize="sm" color="fg.error">
                Nothing is ticked, so nothing could ever satisfy this step.
              </Text>
            ) : null}
          </Box>

          <Stack gap={2}>
            <SubLabel hint="The period is the activity's own dates, not stated here">
              Where it has to have happened
            </SubLabel>
            <NativeSelect.Root size="sm" maxW="md">
              <NativeSelect.Field
                value={
                  task.participationExternalKind !== null
                    ? `kind:${task.participationExternalKind}`
                    : task.participationSeriesId !== null
                      ? `series:${task.participationSeriesId}`
                      : ""
                }
                onChange={(event) => {
                  const value = event.target.value;
                  onChangeTask((t) => ({
                    ...t,
                    participationSeriesId: value.startsWith("series:") ? value.slice(7) : null,
                    participationExternalKind: value.startsWith("kind:") ? value.slice(5) : null,
                  }));
                }}
              >
                <option value="">Anywhere — any occasion at all</option>
                <option value="kind:conference">At a conference (self-reported)</option>
                <option value="kind:course">At a course (self-reported)</option>
                {(series ?? []).map((row) => (
                  <option key={row._id} value={`series:${row._id}`}>At a {row.name}</option>
                ))}
              </NativeSelect.Field>
              <NativeSelect.Indicator />
            </NativeSelect.Root>
          </Stack>
        </Stack>
      )}
    </Stack>
  );
});

/**
 * One band of the task editor.
 *
 * The panel had grown to eight sibling blocks in a flat column, each labelled
 * with the same muted line, so nothing said which controls belonged together
 * and the eye had no rungs to climb. Three bands with a numbered heading and a
 * rule do that work, and the indent under the heading is what makes "these
 * controls are inside this question" visible without a box around everything.
 *
 * The heading is the most prominent text in the band, on purpose. Inner labels
 * are `Field.Label`-sized and muted -- the inversion the reader noticed, where
 * "Named people" sat louder than the section it lived in, came from those two
 * being the other way round.
 */
function TaskSection({
  letter,
  title,
  hint,
  children,
}: {
  /** A, B, C -- lettered, because the TASKS are what carry numbers. */
  letter: string;
  title: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <Stack gap={3}>
      <HStack gap={2} align="baseline" borderBottomWidth="1px" borderColor="border" pb={1.5}>
        <Text
          fontSize="xs"
          fontWeight="bold"
          color="blue.600"
          w="1.1rem"
          textAlign="center"
          flexShrink={0}
        >
          {letter}
        </Text>
        <Text fontSize="sm" fontWeight="semibold">{title}</Text>
        {hint === undefined ? null : (
          <Text fontSize="xs" color="fg.muted">{hint}</Text>
        )}
      </HStack>
      <Stack gap={4} pl={{ base: 0, sm: "1.6rem" }}>{children}</Stack>
    </Stack>
  );
}

/** A quiet label for a control inside a band. Never louder than the band. */
function SubLabel({ children, hint }: { children: React.ReactNode; hint?: string }) {
  return (
    <HStack gap={2} align="baseline">
      <Text fontSize="xs" fontWeight="semibold" textTransform="uppercase" letterSpacing="0.14em" color="fg.muted">
        {children}
      </Text>
      {hint === undefined ? null : <Text fontSize="xs" color="fg.muted">{hint}</Text>}
    </HStack>
  );
}

/**
 * Everybody who signs, in one control.
 *
 * Reviewers are a union of three kinds (model §9) and the editor showed them
 * as six switches plus a separate picker -- which made the rare case (two
 * named people) as prominent as the common one and spread one decision over
 * two controls. One picker matches the data: a list of reviewer specs, with
 * the supervisor and the roles pinned above the people.
 */
export const ReviewerPicker = memo(function ReviewerPicker({
  people,
  task,
  onChange,
  forCopy = false,
}: {
  people: Array<{ _id: string; fullName: string; email: string | null }>;
  task: TaskPeople;
  // As for CompletionSelect: one copy has no "named when assigned" left.
  forCopy?: boolean;
  onChange: (next: { supervisor: boolean; coSupervisor: boolean; namedAtAssignment: boolean; roles: ReviewRole[]; personIds: string[] }) => void;
}) {
  const options = useMemo(
    () => [
      { value: "supervisor", label: "The supervisor", hint: "Resolved per person, from the assignee's group" },
      { value: "coSupervisor", label: "The co-supervisor", hint: "Resolved per person, from the recorded co-supervision" },
      ...(forCopy ? [] : [{ value: "namedAtAssignment", label: "Named when assigned", hint: "Chosen in the assignment drawer" }]),
      ...reviewRoleOptions.map((role) => ({
        value: `role:${role.value}`,
        label: role.label,
        hint: "Whoever holds the role at the time",
      })),
      ...people.map((person) => ({
        value: `person:${String(person._id)}`,
        label: person.fullName,
        hint: person.email ?? undefined,
      })),
    ],
    [people, forCopy],
  );

  const selected = useMemo(
    () => [
      ...(task.reviewerSupervisor ? ["supervisor"] : []),
      ...(task.reviewerCoSupervisor ? ["coSupervisor"] : []),
      ...(task.reviewerNamedAtAssignment ? ["namedAtAssignment"] : []),
      ...task.reviewerRoles.map((role) => `role:${role}`),
      ...task.reviewerPersonIds.map((id) => `person:${id}`),
    ],
    [task.reviewerSupervisor, task.reviewerCoSupervisor, task.reviewerNamedAtAssignment, task.reviewerRoles, task.reviewerPersonIds],
  );

  const tagLabelFor = useCallback((value: string) => {
    if (value === "supervisor") {
      return "Supervisor";
    }
    if (value === "coSupervisor") {
      return "Co-supervisor";
    }
    if (value === "namedAtAssignment") {
      return "Named when assigned";
    }
    if (value.startsWith("role:")) {
      return reviewRoleOptions.find((role) => role.value === value.slice(5))?.label ?? value;
    }
    const person = people.find((candidate) => String(candidate._id) === value.slice(7));
    return person?.fullName ?? "Unknown person";
  }, [people]);

  const handleChange = useCallback((next: string[]) => {
    onChange({
      supervisor: next.includes("supervisor"),
      coSupervisor: next.includes("coSupervisor"),
      namedAtAssignment: next.includes("namedAtAssignment"),
      roles: next.filter((v) => v.startsWith("role:")).map((v) => v.slice(5) as ReviewRole),
      personIds: next.filter((v) => v.startsWith("person:")).map((v) => v.slice(7)),
    });
  }, [onChange]);

  return (
    <MultiSelectTags
      label="Who signs it off"
      hideLabel
      placeholder="The supervisor, a role, or search for a person…"
      emptyMessage="Nothing matches that."
      helperText="Leave empty where the step needs no approval."
      options={options}
      selected={selected}
      onChange={handleChange}
      tagLabelFor={tagLabelFor}
    />
  );
});
